
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type UserRole = 
  | 'admin' 
  | 'manager' 
  | 'sales' 
  | 'qc' 
  | 'finance' 
  | 'customer'
  | 'ho_manager'
  | 'operations_head'
  | 'director'
  | 'ceo'
  | 'sales_head'
  | 'manager_pobs'
  | 'branch_manager'
  | 'store_manager'
  | 'regional_manager';

interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string;
  department?: string;
  level?: number;
  reportingTo?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  login: (role: UserRole, password: string) => boolean;
  loginDemo: (role: UserRole) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      isAuthenticated: false,
      user: null,
      login: (role: UserRole, password: string) => {
        // Check password
        if (password !== 'Admin@123') {
          return false;
        }

        const mockUsers: Record<UserRole, User> = {
          admin: {
            id: '1',
            name: 'System Admin',
            email: 'admin@princejewelry.com',
            role: 'admin',
            department: 'IT',
            level: 1,
          },
          ceo: {
            id: '2',
            name: 'Rajesh Kumar',
            email: 'ceo@princejewelry.com',
            role: 'ceo',
            department: 'Executive',
            level: 10,
          },
          director: {
            id: '3',
            name: 'Priya Sharma',
            email: 'director@princejewelry.com',
            role: 'director',
            department: 'Executive',
            level: 9,
            reportingTo: 'CEO',
          },
          operations_head: {
            id: '4',
            name: 'Amit Patel',
            email: 'operations@princejewelry.com',
            role: 'operations_head',
            department: 'Operations',
            level: 8,
            reportingTo: 'Director',
          },
          ho_manager: {
            id: '5',
            name: 'Sunita Gupta',
            email: 'ho.manager@princejewelry.com',
            role: 'ho_manager',
            department: 'Head Office',
            level: 7,
            reportingTo: 'Operations Head',
          },
          sales_head: {
            id: '6',
            name: 'Vikram Singh',
            email: 'sales.head@princejewelry.com',
            role: 'sales_head',
            department: 'Sales',
            level: 7,
            reportingTo: 'Operations Head',
          },
          regional_manager: {
            id: '7',
            name: 'Neha Agarwal',
            email: 'regional@princejewelry.com',
            role: 'regional_manager',
            department: 'Sales',
            level: 6,
            reportingTo: 'Sales Head',
          },
          manager_pobs: {
            id: '8',
            name: 'Rohit Mehta',
            email: 'pobs@princejewelry.com',
            role: 'manager_pobs',
            department: 'Point of Business',
            level: 5,
            reportingTo: 'Regional Manager',
          },
          branch_manager: {
            id: '9',
            name: 'Kavita Joshi',
            email: 'branch@princejewelry.com',
            role: 'branch_manager',
            department: 'Branch Operations',
            level: 5,
            reportingTo: 'Regional Manager',
          },
          store_manager: {
            id: '10',
            name: 'Deepak Yadav',
            email: 'store@princejewelry.com',
            role: 'store_manager',
            department: 'Store Operations',
            level: 4,
            reportingTo: 'Branch Manager',
          },
          manager: {
            id: '11',
            name: 'Anita Verma',
            email: 'manager@princejewelry.com',
            role: 'manager',
            department: 'General',
            level: 4,
            reportingTo: 'HO Manager',
          },
          sales: {
            id: '12',
            name: 'Ravi Kumar',
            email: 'sales@princejewelry.com',
            role: 'sales',
            department: 'Sales',
            level: 3,
            reportingTo: 'Store Manager',
          },
          qc: {
            id: '13',
            name: 'Pooja Reddy',
            email: 'qc@princejewelry.com',
            role: 'qc',
            department: 'Quality Control',
            level: 3,
            reportingTo: 'HO Manager',
          },
          finance: {
            id: '14',
            name: 'Suresh Iyer',
            email: 'finance@princejewelry.com',
            role: 'finance',
            department: 'Finance',
            level: 3,
            reportingTo: 'HO Manager',
          },
          customer: {
            id: '15',
            name: 'Customer User',
            email: 'customer@princejewelry.com',
            role: 'customer',
            department: 'External',
            level: 0,
          },
        };

        set({
          isAuthenticated: true,
          user: mockUsers[role],
        });
        return true;
      },
      loginDemo: (role: UserRole) => {
        const mockUsers: Record<UserRole, User> = {
          admin: {
            id: '1',
            name: 'System Admin',
            email: 'admin@princejewelry.com',
            role: 'admin',
            department: 'IT',
            level: 1,
          },
          ceo: {
            id: '2',
            name: 'Rajesh Kumar',
            email: 'ceo@princejewelry.com',
            role: 'ceo',
            department: 'Executive',
            level: 10,
          },
          director: {
            id: '3',
            name: 'Priya Sharma',
            email: 'director@princejewelry.com',
            role: 'director',
            department: 'Executive',
            level: 9,
            reportingTo: 'CEO',
          },
          operations_head: {
            id: '4',
            name: 'Amit Patel',
            email: 'operations@princejewelry.com',
            role: 'operations_head',
            department: 'Operations',
            level: 8,
            reportingTo: 'Director',
          },
          ho_manager: {
            id: '5',
            name: 'Sunita Gupta',
            email: 'ho.manager@princejewelry.com',
            role: 'ho_manager',
            department: 'Head Office',
            level: 7,
            reportingTo: 'Operations Head',
          },
          sales_head: {
            id: '6',
            name: 'Vikram Singh',
            email: 'sales.head@princejewelry.com',
            role: 'sales_head',
            department: 'Sales',
            level: 7,
            reportingTo: 'Operations Head',
          },
          regional_manager: {
            id: '7',
            name: 'Neha Agarwal',
            email: 'regional@princejewelry.com',
            role: 'regional_manager',
            department: 'Sales',
            level: 6,
            reportingTo: 'Sales Head',
          },
          manager_pobs: {
            id: '8',
            name: 'Rohit Mehta',
            email: 'pobs@princejewelry.com',
            role: 'manager_pobs',
            department: 'Point of Business',
            level: 5,
            reportingTo: 'Regional Manager',
          },
          branch_manager: {
            id: '9',
            name: 'Kavita Joshi',
            email: 'branch@princejewelry.com',
            role: 'branch_manager',
            department: 'Branch Operations',
            level: 5,
            reportingTo: 'Regional Manager',
          },
          store_manager: {
            id: '10',
            name: 'Deepak Yadav',
            email: 'store@princejewelry.com',
            role: 'store_manager',
            department: 'Store Operations',
            level: 4,
            reportingTo: 'Branch Manager',
          },
          manager: {
            id: '11',
            name: 'Anita Verma',
            email: 'manager@princejewelry.com',
            role: 'manager',
            department: 'General',
            level: 4,
            reportingTo: 'HO Manager',
          },
          sales: {
            id: '12',
            name: 'Ravi Kumar',
            email: 'sales@princejewelry.com',
            role: 'sales',
            department: 'Sales',
            level: 3,
            reportingTo: 'Store Manager',
          },
          qc: {
            id: '13',
            name: 'Pooja Reddy',
            email: 'qc@princejewelry.com',
            role: 'qc',
            department: 'Quality Control',
            level: 3,
            reportingTo: 'HO Manager',
          },
          finance: {
            id: '14',
            name: 'Suresh Iyer',
            email: 'finance@princejewelry.com',
            role: 'finance',
            department: 'Finance',
            level: 3,
            reportingTo: 'HO Manager',
          },
          customer: {
            id: '15',
            name: 'Customer User',
            email: 'customer@princejewelry.com',
            role: 'customer',
            department: 'External',
            level: 0,
          },
        };

        set({
          isAuthenticated: true,
          user: mockUsers[role],
        });
      },
      logout: () => {
        set({
          isAuthenticated: false,
          user: null,
        });
      },
    }),
    {
      name: 'prince-jewelry-auth',
    }
  )
);
